import { useAuth } from '@clerk/clerk-react'
import { useRef, useState } from 'react'

import { ReportSheet } from '@/components/drops/ReportSheet'
import { GlassCard } from '@/components/ui/GlassCard'
import { Icon } from '@/components/ui/Icon'
import { Pill } from '@/components/ui/Pill'
import { useMe } from '@/context/MeProvider'
import { useAuthRedirect } from '@/hooks/useAuthRedirect'
import { ApiError } from '@/lib/api/client'
import { claimListing } from '@/lib/api/listings'
import { cn } from '@/lib/cn'
import { countryName } from '@/data/countries'
import {
  CLAIM_ACTION,
  Clock,
  Flag,
  Globe,
  accentForType,
  iconForCategory,
} from '@/lib/icons'
import { formatExpiry } from '@/lib/time'
import type { PublicListing } from '@/lib/types/listing'

function claimMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) {
      return "Couldn't open this link. Please try again."
    }
    if (err.status === 401) return 'Sign in to claim this link.'
    if (err.status === 403 && err.detail === 'Email not verified') {
      return 'Verify your email to claim a link.'
    }
    if (err.status === 403 && err.detail === 'Account is not active') {
      return 'Your account is suspended.'
    }
    if (err.status === 403 && err.detail === 'Cannot claim your own listing') {
      return "You can't claim your own link."
    }
    if (err.status === 404) return 'This link is no longer available.'
    if (err.status >= 500) {
      return 'Something went wrong claiming this link. Try again.'
    }
    return err.detail || "Couldn't claim this link."
  }
  if (err instanceof TypeError) {
    return 'Network error — check your connection and try again.'
  }
  return "Couldn't claim this link."
}

export function DropCard({
  listing,
  sample = false,
}: {
  listing: PublicListing
  sample?: boolean
}) {
  const accent = accentForType(listing.type)
  const claim = CLAIM_ACTION
  const expiry = formatExpiry(listing.expiresAt)
  const offer = listing.benefitClaim?.trim() || null
  const headingId = `drop-${listing.id}`
  const place = countryName(listing.country)
  const { isSignedIn, getToken } = useAuth()
  const { goToSignIn } = useAuthRedirect()
  const { me, messageFor } = useMe()
  const ownListing =
    me !== null &&
    me.username.toLowerCase() === listing.posterUsername.toLowerCase()
  const [claiming, setClaiming] = useState(false)
  const [claimError, setClaimError] = useState<string | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [reported, setReported] = useState(false)
  const [reportCount, setReportCount] = useState(listing.reportCount)
  const [claimCount, setClaimCount] = useState(listing.claimCount)
  const countedClaim = useRef(false)

  async function handleClaim() {
    setClaimError(null)
    if (!isSignedIn) {
      goToSignIn()
      return
    }
    if (ownListing) {
      setClaimError("You can't claim your own link.")
      return
    }
    const claimGate = messageFor('claim')
    if (claimGate) {
      setClaimError(claimGate)
      return
    }

    setClaiming(true)
    // Open during the click so the popup isn't blocked after the claim request.
    const tab = window.open('about:blank', '_blank')
    if (tab) {
      tab.opener = null
    }
    try {
      const destination = await claimListing(listing.id, getToken)
      if (tab) {
        tab.location.replace(destination)
      } else {
        setClaimError(
          'Allow pop-ups for dizcant to open the link in a new tab.',
        )
      }
      if (!countedClaim.current) {
        countedClaim.current = true
        setClaimCount((count) => count + 1)
      }
    } catch (err) {
      tab?.close()
      if (err instanceof ApiError && err.status === 401) {
        goToSignIn()
      }
      setClaimError(claimMessage(err))
    } finally {
      setClaiming(false)
    }
  }

  return (
    <GlassCard
      as="article"
      interactive
      aria-labelledby={headingId}
      className="relative flex h-full flex-col gap-4 p-6"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={accent}>{listing.type}</Pill>
        {place && (
          <Pill tone="neutral" className="normal-case">
            <Icon icon={Globe} className="size-3" />
            <span className="sr-only">Works in </span>
            {place}
          </Pill>
        )}
        {sample && <Pill tone="neutral">Example</Pill>}
      </div>

      <div className="flex items-center gap-3">
        <span className="bg-surface-container text-primary flex size-10 shrink-0 items-center justify-center rounded-md">
          <Icon icon={iconForCategory(listing.category)} className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3
            id={headingId}
            className="font-display text-headline-md text-fg truncate"
          >
            {listing.brand}
          </h3>
          <p className="font-body text-label-sm text-fg-muted truncate">
            {listing.category ?? 'General'}
          </p>
        </div>
      </div>

      {offer && (
        <p className="font-body text-body-md text-on-surface font-semibold text-pretty">
          {offer}
        </p>
      )}

      <div className="border-glass-border mt-auto flex items-center justify-between border-t pt-4">
        <span
          className={cn(
            'font-body text-label-sm inline-flex items-center gap-1.5',
            expiry.urgent ? 'text-error' : 'text-fg-muted',
          )}
        >
          <Icon icon={Clock} className="size-4" />
          {expiry.label}
        </span>
        {sample ? (
          <span className="font-body text-label-caps text-fg-muted inline-flex min-h-11 shrink-0 items-center uppercase">
            Preview
          </span>
        ) : ownListing ? (
          <span className="font-body text-label-caps text-fg-muted inline-flex min-h-11 shrink-0 items-center uppercase">
            Posted
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              void handleClaim()
            }}
            disabled={claiming}
            className="font-body text-label-caps text-primary hover:text-primary-fixed-dim inline-flex min-h-11 items-center gap-1.5 rounded-sm uppercase transition-colors disabled:opacity-50"
          >
            {claiming ? 'Claiming…' : claim.label}
            <Icon icon={claim.icon} className="size-4" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="font-body text-label-sm text-fg-muted">
          {claimCount} claims
          {reportCount > 0 ? ` · ${reportCount} reports` : ''}
        </p>
        {!sample &&
          !ownListing &&
          (reported ? (
            <span className="font-body text-label-sm text-fg-muted">
              Report sent
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setReportOpen(true)
              }}
              className="font-body text-label-sm text-fg-muted hover:text-error inline-flex min-h-11 items-center gap-1.5 rounded-sm transition-colors"
            >
              <Icon icon={Flag} className="size-3.5" />
              Report
            </button>
          ))}
      </div>

      {claimError && (
        <p className="font-body text-label-sm text-error" role="alert">
          {claimError}
        </p>
      )}

      {!sample && (
        <ReportSheet
          listingId={listing.id}
          brand={listing.brand}
          open={reportOpen}
          onClose={() => {
            setReportOpen(false)
          }}
          onReported={() => {
            setReported(true)
            setReportCount((count) => count + 1)
          }}
        />
      )}
    </GlassCard>
  )
}
